import { RUSSIAN_LANGUAGE } from "../api/config"
import { membershipOf } from "../api/queries"
import { useAuth } from "./AuthContext"
import { Roles } from "./session"

/**
 * Права — повтор серверных правил для UI.
 *
 * Источник истины остаётся на бэке: `RoleAuthorize` на действии плюс проверка
 * принадлежности группы в `*ControllerAuthHelper`. Здесь те же условия, но
 * только для ролей, которые доходят до рижского интерфейса. Всё проверено
 * запросами к локальному стенду под ролью `ContentManagerRg` — итог в
 * `API-CONTRACT.md`, раздел «Что из этого доступно Риге».
 */

/** Права, не зависящие от конкретной группы. */
export function useAccess() {
	const { hasRole } = useAuth()

	/** Контент-менеджер: Латвии или Риги. */
	const isManager =
		hasRole(Roles.ContentManagerLv) || hasRole(Roles.ContentManagerRg)

	return {
		isManager,
		isLv: hasRole(Roles.ContentManagerLv),
		isRg: hasRole(Roles.ContentManagerRg),
		/** Переводчик: справочник ему открыт, но только построчной записью. */
		isTranslator: hasRole(Roles.Translator),
		/** Администратор. Экран «Каталоги и сайты» (`/publish`) открыт только
		 *  ему — решение заказчика: там назначают товары в каталоги и заводят
		 *  новые сайты с боевыми реквизитами ФТП и БД, промах стоит дорого. В
		 *  карточке группы вкладка «Сайт» остаётся всем, кто может публиковать. */
		isAdmin: hasRole(Roles.Admin),
		/** Загрузка на сайт — `WebsiteController.cs:259,285,300`. */
		canPublish: isManager || hasRole(Roles.Admin),
	}
}

/**
 * Права на карточку группы.
 *
 * `none` — группа без прайс-группы. В списке Рига таких не видит, попасть в
 * такую можно только сразу после создания: прайс-группа появляется вместе с
 * первым товаром (`ProductAppender.cs:98`).
 */
export function useGroupRights(
	priceGroupId: number | null,
	languageId: number
) {
	const { isLv, isManager } = useAccess()

	const membership = membershipOf(priceGroupId)
	const own = membership === "own"
	const mine = own || membership === "none"
	const isRussian = languageId === RUSSIAN_LANGUAGE

	return {
		/** Название: у своих групп на любом языке, иначе на любом, кроме русского. */
		name: isManager && (own || !isRussian),
		/** Описания. У РФ-групп их ведёт российская сторона. */
		description: isManager && mine,
		/** Флаги «проверено»: только у своих — у ничейной бэк их менять не даёт. */
		checks: isManager && own,

		/** Добавление товара в карточку. */
		addProduct: isManager && mine,
		/** Удаление: роли Rg нет в RoleAuthorize российского кода — только Lv. */
		removeProduct: isLv && mine,
		/** Замена товара: helper латвийских групп требует именно Lv. */
		replaceProduct: isLv && mine,
		/** Порядок товаров в карточке. */
		sortProducts: isManager && mine,

		/** Значения атрибутов в ячейках. */
		values: isManager && mine,
		/** Состав и порядок колонок. У ничейной группы бэк требует
		 *  ContentManagerRF, поэтому колонки набираются после первого товара. */
		columns: isManager && own,
		/** Признак «важный» и приоритет среди важных. */
		important: isManager && mine,
		/** Главный атрибут карточки. Правится и в РФ-группах. */
		mainAttribute: isManager,

		/** Пиктограммы. У своих групп их ведут Lv и Rg; у РФ-групп и ничейных бэк
		 *  требует `ContentManagerRF` или фотографа, а этих ролей у Риги нет —
		 *  значит там только просмотр (`PictogramControllerAuthHelper`). */
		pictograms: isManager && own,

		/** Фото: добавление и удаление. У латвийских и ничейных групп пишут Lv и
		 *  Rg, у РФ-групп `ImageControllerAuthHelper` требует `ContentManagerRF`
		 *  или фотографа. Замену файла и смену типа (`PUT /api/images*`)
		 *  `RoleAuthorize` Риге не даёт вовсе — только убрать и добавить заново. */
		images: isManager && mine,

		/** Состав набора (комплектация): добавление, замена, удаление и порядок
		 *  товаров-компонентов. У своих наборов пишут Lv и Rg; у РФ-наборов
		 *  `ToolsetControllerAuthHelper` требует именно `ContentManagerRF` — там
		 *  только просмотр, `possible-toolset-products` для них не запрашиваем. */
		toolsetControl: isManager && own,
		/** Раздел (пиктограмма) товара внутри состава — то же правило. */
		toolsetPictograms: isManager && own,

		/** Категории на сайте. Правятся и в РФ-группах: `RoleAuthorize` на всех
		 *  четырёх действиях перечисляет `Rg`, а `CategoryControllerAuthHelper`
		 *  ограничивает только чужих — латвийские группы он закрывает для всех,
		 *  кроме `Lv` и `Rg` (`CategoryControllerAuthHelper.cs:26-92`).
		 *  Печатный каталог там же требует `ContentManagerRF` — его не показываем. */
		webCategories: isManager,

		/** Создание группы: RoleAuthorize(RF, Lv, Rg), только на русском языке. */
		createGroup: isManager,
	}
}

export type GroupRights = ReturnType<typeof useGroupRights>
